<template>
  <div
    v-if="liquid.blocks.length > 0 && productMatchLink"
    class="
      product-box
      critical-component-clear
      section-padding-top
      section-padding-bottom
    "
    :class="[
      liquid.marginBottom,
      liquid.marginTop,
    ]"
  >
    <div class="container">
      <div class="product-box__heading">
        <component
          :is="liquid.eyebrowTag"
          v-if="liquid.eyebrow"
          class="product-box__eyebrow text-label-xs text-label-s-desktop"
          v-text="liquid.eyebrow"
        />

        <component
          :is="liquid.titleTag"
          v-if="liquid.title"
          :id="`section-title-${liquid.sectionId}`"
          class="product-box__title text-heading-xl text-heading-3xl-desktop"
          v-text="liquid.title"
        />
      </div>

      <div class="product-box__container">
        <!-- Switcher copy -->
        <div
          v-if="isTablet"
          ref="switcherContainer"
          class="
            product-box__switcher-copy
            product-box__switcher-copy--desktop
            background-pseudo-inherit
          "
        >
          <template
            v-for="(block, index) in liquid.blocks"
            :key="index"
          >
            <transition-group name="product-box">
              <div
                v-if="index === activeIndex"
                class="product-box__switcher-copy-wrapper"
              >
                <product-box-copy
                  :title="block.title"
                  :title-tag="slideTitleTag"
                  :description="block.description"
                  :footer="block.footer"
                  :accordion="block.accordion"
                  :toggle-index="toggleIndex"
                  @accordionToggle="handleAccordionToggle"
                />
              </div>
            </transition-group>
          </template>
        </div>

        <!-- Swiper -->
        <swiper
          class="
            product-box__swiper
          "
          :loop="true"
          :loop-additional-slides="3"
          :breakpoints="breakpoints"
          :modules="modules"
          :navigation="navigation"
          :pagination="pagination"
          :slide-to-clicked-slide="true"
          :speed="600"
          @init="handleInit"
          @slide-change="handleSlideChange"
        >
          <swiper-slide
            v-for="(block, index) in liquid.blocks"
            :id="`product-box-${index}`"
            :key="index"
            class="
              product-box__slide
            "
          >
            <responsive-image
              :block-class="'product-box'"
              :image="block.image"
              :lazyload="true"
              :ratio="!isTablet ? block.image.ratio : block.image.ratioDesktop"
            />

            <div
              v-if="!isTablet"
              class="
                product-box__slide-copy
                product-box__slide-copy--mobile
              "
            >
              <div
                class="product-box__slide-copy-inner"
                js-product-box="content"
              >
                <product-box-copy
                  :title="block.title"
                  :title-tag="slideTitleTag"
                  :description="block.description"
                  :footer="block.footer"
                  :accordion="block.accordion"
                  :toggle-index="toggleIndex"
                  :index="index"
                  @accordionToggle="handleAccordionToggle"
                />
              </div>
            </div>
          </swiper-slide>
        </swiper>
      </div>

      <!-- Swiper navigation -->
      <swiper-navigation
        :section-id="liquid.sectionId"
        :block-class="'product-box'"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product Box (product-box)
 * -----------------------------------------------------------------------------
 * Component for Product Box.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { mapState } from 'vuex'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y, Navigation, Pagination } from 'swiper'
import SwiperNavigation from '~async/swiper-navigation/swiper-navigation'
import { values } from '~/config/breakpoints'
import { convertNextTitleTag } from '~/helpers/convert'
import ProductBoxCopy from '~async/product-box/product-box-copy'

export default {
  name: 'ProductBox',

  components: {
    Swiper,
    SwiperSlide,
    SwiperNavigation,
    ProductBoxCopy,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        marginBottom: '',
        marginTop: '',
        backgroundColor: '',
        textColor: '',
        sectionId: '',
        eyebrowTag: '',
        eyebrow: '',
        titleTag: '',
        title: '',
        blocks: [],
      }),
    },
  },

  setup(props) {
    const breakpoints = {}

    breakpoints[values.xs] = {
      slidesPerView: 1,
      spaceBetween: 10,
    }

    breakpoints[values.m] = {
      slidesPerView: 1,
      spaceBetween: 8,
    }

    breakpoints[values.xl] = {
      slidesPerView: 2,
      spaceBetween: 8,
    }

    return {
      breakpoints,
      modules: [
        A11y,
        Navigation,
        Pagination,
      ],
      navigation: {
        prevEl: `.swiper-button-prev-${props.liquid.sectionId}`,
        nextEl: `.swiper-button-next-${props.liquid.sectionId}`,
      },
      pagination: {
        clickable: true,
        el: `.swiper-pagination-${props.liquid.sectionId}`,
        type: 'bullets',
      },
    }
  },

  data() {
    return {
      swiper: null,
      staticContent: [],
      staticContainer: null,
      activeIndex: 0,
      toggleIndex: {},
      productMatchLink: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      screen: (state) => state.index.screen,
    }),

    /**
     * Compute open quote.
     */
    openQuote() {
      return '“'
    },

    slideTitleTag() {
      const nextTag = convertNextTitleTag(this.liquid.titleTag)
      return nextTag
    },

    isTablet() {
      return this.screen.width >= values.m
    },
  },

  watch: {
    screen() {
      this.setStaticContainer()
      this.buildSidePocket()
    },
  },

  mounted() {
    this.setEventBusListeners()
  },

  methods: {

    /**
     * Set EventBus listener for form submit success.
     * - Can't use Vue emit as function is set in <head>
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('update-request-object', (product) => {
        this.product = product
        this.productId = product.id
        this.matchProductTag()
      })
    },

    /**
     * Check if product is linked to product box
     */
    matchProductTag() {
      this.productMatchLink = this.product.tags.includes(this.liquid.productTagLink) || this.liquid.product === this.productId
    },

    /**
     * Callback that updates global accordion index.
     * @param {Object} toggleIndex - data from clicked accordion item.
     */
    handleAccordionToggle(toggleIndex) {
      this.toggleIndex = toggleIndex
    },

    /**
     * Build the element that hides the carousel from overflowing
     * the left side of the static container
     * - Apply hex color if none is selected from brand color dropdown
     */
    buildSidePocket() {
      if (!this.isTablet || !this.staticContainer) {
        return
      }

      const pocketHeight = this.staticContainer.getBoundingClientRect().height
      const pocketWidth = this.staticContainer.getBoundingClientRect().left


      document.documentElement.style.setProperty(
        '--pocket-pos-product-box',
        `${pocketWidth}px`,
      )

      document.documentElement.style.setProperty(
        '--pocket-height-product-box',
        `${pocketHeight}px`,
      )

      if (this.liquid.backgroundColor.includes('--none')) {
        document.documentElement.style.setProperty(
          '--pocket-color-product-box',
          this.liquid.backgroundColorHex,
        )
      }
    },

    /**
     * Callback after swiper initialized
     */
    handleInit(swiper){
      this.swiper = swiper
      this.setStaticContainer()
      this.buildSidePocket()
      this.updateStaticContent()

      if (this.swiper.slides.length > 0 && !this.isTablet) {
        this.updateSlideContentHeight(this.swiper.slides)
      }
    },

    /**
     * Set the slide content to consistent height.
     * @param {Array} slides - all slides
     */
    updateSlideContentHeight(slides) {
      const slideContent = slides.map((slide) => {
        if (slide.childNodes[1]?.childNodes[0]) {
          return slide.childNodes[1]?.childNodes[0]?.clientHeight
        }
      }).filter((slide) => {
        return slide !== undefined
      })

      if (slideContent.length < 1) {
        return
      }

      const minHeight = Math.max(...slideContent)
      document.documentElement.style.setProperty(
        '--product-box-slide-content-height',
        `${minHeight}px`,
      )
    },

    /**
     * Callback from slide change
     */
    handleSlideChange() {
      if (this.isTablet) {
        this.updateStaticContent()
      }
    },

    /**
     * Cache static container element
     */
    setStaticContainer() {
      this.staticContainer = this.$refs.switcherContainer
    },

    /**
     * Update the switcher content container
     */
    updateStaticContent() {
      if (!this.isTablet  || !this.swiper) {
        return
      }

      this.activeIndex = this.swiper.realIndex
    },
  },
}
</script>

<style lang="scss">
@import './product-box';
</style>
